/**
 * Graphic service for API communication with tactical graphic endpoints.
 *
 * Coordinates in `points` are `[lng, lat]` (GeoJSON axis order) — the same order
 * used by the renderer's control-point string, the `graphics.points` column and
 * the CoT `points` attribute, so they are never swapped along the way.
 */

import apiClient from '../api/client';

/** Symbol parameters, as ground distances in metres. */
export interface GraphicParams {
    width_m?: number;
    head_length_m?: number;
    [param: string]: number | undefined;
}

export interface GraphicResponse {
    id: string;
    event_id: string;
    owner_id: string | null;
    faction_id: string | null;
    /** Parent unit this graphic is attached to, or null. */
    unit_id: string | null;
    /** Parent marker this graphic is attached to, or null. */
    marker_id: string | null;
    name: string | null;
    sidc: string;
    points: number[][];
    params: GraphicParams;
    is_deleted: boolean;
    created_at: string;
    updated_at: string;
}

export interface GraphicListResponse {
    graphics: GraphicResponse[];
    total: number;
}

export interface GraphicCreate {
    /** Client-generated UUID, so a retried create is idempotent. */
    id?: string;
    name?: string | null;
    sidc: string;
    faction_id?: string | null;
    unit_id?: string | null;
    marker_id?: string | null;
    points: number[][];
    params?: GraphicParams;
}

export interface GraphicUpdate {
    name?: string | null;
    sidc?: string;
    faction_id?: string | null;
    unit_id?: string | null;
    marker_id?: string | null;
    points?: number[][];
    params?: GraphicParams;
}

/**
 * Get graphics for an event.
 *
 * Omitting `factionId` returns only event-wide graphics (`faction_id IS NULL`),
 * matching the markers endpoint. `unitId` / `markerId` narrow to one parent's
 * attached graphics.
 */
export async function getGraphics(
    eventId: string,
    factionId?: string,
    unitId?: string,
    markerId?: string,
): Promise<GraphicListResponse> {
    const params: Record<string, string> = {};
    if (factionId) params.faction_id = factionId;
    if (unitId) params.unit_id = unitId;
    if (markerId) params.marker_id = markerId;
    const response = await apiClient.get<GraphicListResponse>(
        `/api/v1/events/${eventId}/graphics`,
        { params },
    );
    return response.data;
}

/**
 * Create a new tactical graphic.
 *
 * Supply `data.id` (a client-generated UUID) so a retry after a failed request
 * returns the existing graphic rather than creating a duplicate.
 */
export async function createGraphic(
    eventId: string,
    data: GraphicCreate,
): Promise<GraphicResponse> {
    const response = await apiClient.post<GraphicResponse>(
        `/api/v1/events/${eventId}/graphics`,
        data,
    );
    return response.data;
}

/**
 * Update an existing graphic. `points` and `params` replace the previous values
 * wholesale — there is no per-vertex patch.
 */
export async function updateGraphic(
    eventId: string,
    graphicId: string,
    data: GraphicUpdate,
): Promise<GraphicResponse> {
    const response = await apiClient.patch<GraphicResponse>(
        `/api/v1/events/${eventId}/graphics/${graphicId}`,
        data,
    );
    return response.data;
}

/**
 * Soft delete a graphic. The backend publishes a tombstone, so other clients
 * drop it without a refetch.
 */
export async function deleteGraphic(
    eventId: string,
    graphicId: string,
): Promise<void> {
    await apiClient.delete(`/api/v1/events/${eventId}/graphics/${graphicId}`);
}
